import React from 'react';
import { Link } from 'react-router-dom';

import { useQuery, useMutation, useQueryClient } from '@sopranodesign/pulse-hooks';
import {
  PageWrapper,
  Placeholder,
  Flex,
  Divider,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableActions,
  OrderableTableRow,
  useOrderableTableReducer,
  StretchPanel as Panel,
  FormHeading,
  useNotification
} from '@sopranodesign/pulse-components';
import { PermissionFilter } from '@sopranodesign/pulse-auth';

import { McpProviderDetails } from '../Components/index';
import { updateProvider, deleteSkill } from '../api/index';

const EditMcpProvider = ({ match }) => {
  const { showSuccess } = useNotification();
  const queryClient = useQueryClient();

  const id = match.params.id;

  const query = useQuery({ queryKey: [`mcp-skill-providers/${id}`] });

  const skillsQuery = useQuery({
    queryKey: ['mcp-skills'],
    select: data => data.data.filter(s => s.mcp_skill_provider_id === Number(id))
  });

  const mutation = useMutation({
    mutationFn: updateProvider,
    onSuccess: () => {
      showSuccess('Provider updated');
      queryClient.invalidateQueries(`mcp-skill-providers/${id}`);
      queryClient.invalidateQueries('mcp-skill-providers');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSkill,
    onSuccess: () => {
      showSuccess('Skill deleted');
      queryClient.invalidateQueries('mcp-skills');
    }
  });

  const skillsOrder = useOrderableTableReducer({ id: 'mcp-provider-skills' });

  const sortedSkills = React.useMemo(() => {
    const data = skillsQuery.data ?? [];
    if (!skillsOrder.order || !skillsOrder.direction) { return data; }
    return [...data].sort((a, b) => {
      let aVal = a[skillsOrder.order];
      let bVal = b[skillsOrder.order];
      if (typeof aVal === 'boolean') {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      } else {
        aVal = String(aVal ?? '').toLowerCase();
        bVal = String(bVal ?? '').toLowerCase();
      }
      if (aVal < bVal) { return skillsOrder.direction === 'ascending' ? -1 : 1; }
      if (aVal > bVal) { return skillsOrder.direction === 'ascending' ? 1 : -1; }
      return 0;
    });
  }, [skillsQuery.data, skillsOrder.order, skillsOrder.direction]);

  const handleDeleteSkill = skillId => () => deleteMutation.mutate(skillId);

  return (
    <PageWrapper
      header="Edit MCP Provider"
      subheader="Update provider settings">
      {query.isLoading && <Placeholder />}
      {query.isSuccess && (
        <McpProviderDetails
          handleSubmit={form => mutation.mutate({ id, ...form })}
          loading={mutation.isPending}
          initialProvider={query.data.data}
        />
      )}

      <Divider tall />

      <Panel>
        <Flex between center pad>
          <FormHeading>Skills</FormHeading>
          <PermissionFilter can="create mcp skills">
            <Button
              as={Link}
              to={`/mcp/providers/${id}/skills/create`}
              variant="primary"
              aria-label="Add skill">
              + Add Skill
            </Button>
          </PermissionFilter>
        </Flex>

        <Table loading={skillsQuery.isLoading} loadingColumns={3}>
          <TableHead>
            <OrderableTableRow
              rows={[
                { label: 'Name', sort: 'name' },
                { label: 'Description', sort: 'description' },
                { label: 'Status', sort: 'enabled' },
                null
              ]}
              {...skillsOrder.props}
            />
          </TableHead>
          <TableBody>
            {sortedSkills.map(skill => (
              <TableRow key={skill.id}>
                <TableCell>{skill.name}</TableCell>
                <TableCell>{skill.description}</TableCell>
                <TableCell>{skill.enabled ? 'Enabled' : 'Disabled'}</TableCell>
                <TableActions>
                  <Button
                    as={Link}
                    to={`/mcp/skills/${skill.id}`}
                    icon="pencilAlt"
                    aria-label="Edit skill"
                  > Edit </Button>
                  <PermissionFilter can="delete mcp skills">
                    <Button
                      icon="trash"
                      colour="danger"
                      aria-label="Delete skill"
                      onClick={handleDeleteSkill(skill.id)}
                    > Delete </Button>
                  </PermissionFilter>
                </TableActions>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
    </PageWrapper>
  );
};

export default EditMcpProvider;
